import React, { useEffect, useState } from 'react';
import { useMerchants } from '../../../store/merchnts/merchants-context';
import { IMerchantData } from '../../../interfaces/merchant';
import { exportMerchants, fetchMerchants } from '../../../store/merchnts/actions/merchant';
import { TablesNames } from '../../../store/tables/tables-context';
import { Table } from '../../table/Table';
import { useNavigate } from 'react-router-dom';
import { MERCHANTS_TABS } from '../../../utils/merchantUtils';
import { IMerchantsFilters, MerchantsListFilters } from '../MerchantsListFilters';

import './MerchantsListComponent.scss';
import {
	fetchIntegrationPartnersList,
	fetchWhiteListedPartnersList,
} from '../../../store/merchnts/actions/partners';
import { IAuthState } from '../../../redux/AuthRedux';
import { shallowEqual, useSelector } from 'react-redux';
import { RootState } from '../../../../setup';
import { checkRoles, CLAIMS } from '../../../utils/roles-helper';
import { useLocation } from 'react-router';
import { IMerchantPaginationData } from '../../../store/merchnts/merchant-interfaces';
import { ExportCSVButton } from '../../form/ExportCSVButton';

export interface IPreparedMerchantDataProps {
	contactName: string,
	id: string,
	date: string,
	merchantName: string,
	email: string,
	phone: string,
	status: string,
	partnerName: string,
	integrationPartnerName: string,
}

export const MerchantsListComponent = (): JSX.Element => {
	const navigate = useNavigate();
	const { search } = useLocation();

	const authUser: IAuthState = useSelector<RootState>(({ auth }) => auth, shallowEqual);
	const { merchantState, dispatch } = useMerchants();
	const { chosenMerchant } = merchantState;

	const [status, setStatus] = useState<string>('1');
	const [q, setQ] = useState<string>('');
	const [pid, setPid] = useState<string>('');
	const [integrationPartnerId, setIntegrationPartnerId] = useState<string>('');

	const [merchantsList, setMerchantsList] = useState<IMerchantPaginationData>(null);
	const [isMerchantListLoading, setIsMerchantListLoading] = useState<boolean>(true);
	const [isExportLoading, setExportLoading] = useState<boolean>(false);
	const [searchFilters, setSearchFilters] = useState<IMerchantsFilters | null>(null);

	useEffect(() => {
		if (checkRoles(authUser, CLAIMS.SUPER_ADMIN)) {
			void fetchWhiteListedPartnersList(dispatch);
		}
		if (checkRoles(authUser, CLAIMS.SUPER_ADMIN_AND_ONLY_PARTNER_ADMIN)) {
			void fetchIntegrationPartnersList(dispatch);
		}
	}, []);

	const preparedTableData: IPreparedMerchantDataProps[] = (merchantsList?.rows || [])?.map((el: IMerchantData): IPreparedMerchantDataProps => ({
		contactName: `${el?.primaryContact?.firstName ?? ''} ${el?.primaryContact?.lastName ?? ''}`,
		id: el?.id,
		date: el?.createdOn,
		merchantName: el?.legalEntityName ?? '',
		email: el?.primaryContact?.email ?? '',
		phone: el?.primaryContact?.phone ?? '',
		status: el?.statusName ?? '',
		partnerName: el?.partner?.name ?? '',
		integrationPartnerName: el?.integrationPartner?.name ?? ''
	}));

	const rowActions = (rowData): JSX.Element => {
		const path = `/accounts/merchants/${rowData?.original?.id}/${MERCHANTS_TABS.BUSINESS}`;
		return (
			<div
				className="text-muted cursor-pointer text-center fw-bolder fs-7"
				onClick={(): void => navigate(path, { state: { search }})}
			>
				View
			</div>

		);
	};

	const getSearchParams = (search: IMerchantsFilters): IMerchantsFilters => {
		let searchParams: IMerchantsFilters = {};

		if (Object.keys(search).length) {
			searchParams = { ...search };

			setQ(searchParams?.q ?? '');
			setPid(searchParams?.pid ?? '');
			setIntegrationPartnerId(searchParams?.integrationPartnerId ?? '');

			switch (search?.status) {
				case 'true':
					setStatus('1');
					searchParams.status = '1';
					break;
				case 'false':
					setStatus('2');
					searchParams.status = '2';
					break;
				case 'null':
					setStatus('');
					delete searchParams.status;
					break;
				default:
					setStatus('1');
					break;
			}

		} else {
			searchParams = {
				status: '1',
				mid: chosenMerchant,
				pageNumber: 1,
				pageSize: 10,
			};

		}

		return searchParams;
	};

	const listener = async (search: IMerchantsFilters): Promise<void> => {
		if (!chosenMerchant && !search?.mid) {
			const merchantId = localStorage.getItem('chosenMerchant');
			if (merchantId) {
				return;
			}
		}

		setIsMerchantListLoading(true);

		setSearchFilters(search);
		const searchParams = getSearchParams(search);

		const data = await fetchMerchants(searchParams);

		setMerchantsList(data);

		setIsMerchantListLoading(false);
	};

	const handleExportCSV = async (): Promise<void> => {
		setExportLoading(true);

		searchFilters.pageSize = merchantsList?.totalItemCount ?? 10;

		try {
			const searchParams = getSearchParams(searchFilters);

			await exportMerchants(searchParams);
			setExportLoading(false);
		} catch {
			setExportLoading(false);
		}
	};

	return (
		<>
			<MerchantsListFilters
				status={status}
				setStatus={setStatus}
				q={q}
				setQ={setQ}
				pid={pid}
				setPid={setPid}
				integrationPartnerId={integrationPartnerId}
				setIntegrationPartnerId={setIntegrationPartnerId}
				isMerchantListLoading={isMerchantListLoading}
			/>

			<Table
				name={TablesNames.merchantTableData}
				data={preparedTableData}
				isLoading={isMerchantListLoading}
				totalItemCount={merchantsList?.totalItemCount}
				enableRowActions={true}
				rowActions={rowActions}
				actionColumnsSize={50}
				queryParamsListener={listener}
				toolbarCustomAction={
					<ExportCSVButton
						orderTotalItemCount={merchantsList?.totalItemCount ?? 0}
						isExportCSVLoading={isExportLoading}
						exportCSV={async (): Promise<void> => await handleExportCSV()}
					/>
				}
			/>
		</>
	);
};
